import type { NatureLigne } from './types'

/**
 * Facturation MENSUELLE des clients PME au forfait (migration 0032) — logique PURE, testée.
 *
 * Les montants sont calculés PAR LA BASE (`apercu_factures_mois` / `generer_factures_mois`) : ce module ne
 * recalcule rien, il met en forme le rapport, nomme les natures de ligne et traduit les refus.
 */

/** Nature de chaque ligne de facture, en toutes lettres. */
export const LIBELLE_NATURE: Record<NatureLigne, string> = {
  course: 'Courses facturées à l’unité',
  course_incluse: 'Courses incluses au forfait',
  forfait: 'Forfaits mensuels',
  option_longue_distance: 'Option longue distance',
  depassement: 'Dépassements (courses au-delà du forfait)',
  supplement_horaire: 'Suppléments hors créneau',
  longue_distance: 'Kilomètres de longue distance',
  majoration_tension: 'Majorations « forte demande » (courses incluses)',
}

/** Une facture mensuelle (générée ou prévue), telle que le rapport de la base la décrit. */
export interface FactureMensuelle {
  client_id: string
  client: string
  /** Absent d'un aperçu : rien n'est écrit. */
  invoice_id: string | null
  number: string | null
  plan_code: string
  forfait: string
  /** Courses incluses par mois ; `null` = illimité. */
  quota: number | null
  courses: number
  incluses: number
  depassements: number
  forfait_ttc: number
  option_ttc: number
  supplement_ttc: number
  longue_distance_ttc: number
  /** Majorations « forte demande » des courses incluses (0043) ; 0 si la base n'a pas la 0043. */
  majoration_ttc: number
  depassement_ttc: number
  total_ht: number
  tva: number
  total_ttc: number
}

export type MotifIgnore = 'sans_abonnement' | 'deja_facturee' | 'pas_a_echeance' | 'rien_a_facturer' | 'erreur'

export interface ClientIgnore {
  client_id: string
  client: string
  motif: MotifIgnore
  detail: string | null
  /** Courses livrées du mois restées non facturées pour ce client. */
  courses_non_facturees: number
}

export interface RapportFacturation {
  /** Premier jour du mois facturé, AAAA-MM-JJ. */
  mois: string
  libelle_mois: string
  /** Vrai si les factures ont été écrites ; faux pour un aperçu. */
  ecrit: boolean
  forcer: boolean
  factures: FactureMensuelle[]
  ignores: ClientIgnore[]
  total_ttc: number
}

/** Pourquoi un client est sauté — la phrase que l'écran affiche. */
export const LIBELLE_MOTIF: Record<MotifIgnore, string> = {
  sans_abonnement: 'Pas d’abonnement pour ce mois',
  deja_facturee: 'Déjà facturé',
  pas_a_echeance: 'Pas encore à échéance',
  rien_a_facturer: 'Rien à facturer (total nul)',
  erreur: 'Erreur — facture non écrite',
}

const nombre = (v: unknown): number => (v == null || v === '' ? 0 : Number(v))
const texte = (v: unknown): string => (typeof v === 'string' ? v : '')
const optionnel = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null)

/**
 * Lit la réponse jsonb de la base. Tolérant sur la forme (jamais de `undefined` qui se propage jusqu'à l'écran),
 * strict sur le sens : un motif inconnu devient `erreur`, pour ne rien cacher.
 */
export function lireRapport(brut: unknown): RapportFacturation {
  const o = (brut && typeof brut === 'object' ? brut : {}) as Record<string, unknown>
  const liste = (v: unknown): Record<string, unknown>[] => (Array.isArray(v) ? (v as Record<string, unknown>[]) : [])
  const factures = liste(o.factures).map<FactureMensuelle>((f) => ({
    client_id: texte(f.client_id),
    client: texte(f.client),
    invoice_id: optionnel(f.invoice_id),
    number: optionnel(f.number),
    plan_code: texte(f.plan_code),
    forfait: texte(f.forfait),
    quota: f.quota == null ? null : nombre(f.quota),
    courses: nombre(f.courses),
    incluses: nombre(f.incluses),
    depassements: nombre(f.depassements),
    forfait_ttc: nombre(f.forfait_ttc),
    option_ttc: nombre(f.option_ttc),
    supplement_ttc: nombre(f.supplement_ttc),
    longue_distance_ttc: nombre(f.longue_distance_ttc),
    majoration_ttc: f.majoration_ttc == null ? 0 : nombre(f.majoration_ttc),
    depassement_ttc: nombre(f.depassement_ttc),
    total_ht: nombre(f.total_ht),
    tva: nombre(f.tva),
    total_ttc: nombre(f.total_ttc),
  }))
  const motifs = Object.keys(LIBELLE_MOTIF)
  const ignores = liste(o.ignores).map<ClientIgnore>((i) => ({
    client_id: texte(i.client_id),
    client: texte(i.client),
    motif: (motifs.includes(texte(i.motif)) ? texte(i.motif) : 'erreur') as MotifIgnore,
    detail: optionnel(i.detail),
    courses_non_facturees: nombre(i.courses_non_facturees),
  }))
  return {
    mois: texte(o.mois),
    libelle_mois: texte(o.libelle_mois),
    ecrit: o.ecrit === true,
    forcer: o.forcer === true,
    factures,
    ignores,
    total_ttc: nombre(o.total_ttc),
  }
}

/** « 2026-09 » (valeur d'un <input type="month">) → « 2026-09-01 ». Vide si illisible. */
export function moisDepuisChamp(valeur: string): string {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(valeur) ? `${valeur}-01` : ''
}

/** Le mois proposé par défaut : LE MOIS PRÉCÉDENT, le seul qui soit terminé, au format <input type="month">. */
export function moisPrecedent(maintenant: Date = new Date()): string {
  const d = new Date(Date.UTC(maintenant.getUTCFullYear(), maintenant.getUTCMonth() - 1, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

/** Le dernier mois facturable : un mois n'est facturable qu'une fois son dernier jour passé (heure de Bamako, UTC). */
export function moisFacturable(valeur: string, maintenant: Date = new Date()): boolean {
  const debut = moisDepuisChamp(valeur)
  if (!debut) return false
  const [a, m] = debut.split('-').map(Number)
  const dernierJour = new Date(Date.UTC(a ?? 0, m ?? 1, 0))
  const aujourdhui = new Date(Date.UTC(maintenant.getUTCFullYear(), maintenant.getUTCMonth(), maintenant.getUTCDate()))
  return dernierJour.getTime() < aujourdhui.getTime()
}

const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']

/** « 2026-09-01 » ou « 2026-09 » → « septembre 2026 ». */
export function libelleMois(iso: string): string {
  const [a, m] = iso.split('-').map(Number)
  const nom = MOIS_FR[(m ?? 0) - 1]
  return nom && a ? `${nom} ${a}` : iso
}

/** Les refus de la base (`raise exception '<code>'`), en français. */
const MESSAGES_FACTURATION: Record<string, string> = {
  forbidden: 'Seuls le propriétaire et le comptable génèrent les factures du mois.',
  mois_requis: 'Choisissez un mois.',
  mois_non_termine: "On ne facture qu'un mois terminé : ses dernières livraisons ne sont pas toutes connues.",
  org_non_operatrice: "La facturation mensuelle n'existe que pour l'organisation opératrice.",
}

export function traduireErreurFacturation(erreur: unknown): string {
  const o = (erreur && typeof erreur === 'object' ? erreur : {}) as { message?: unknown }
  const message = typeof o.message === 'string' ? o.message : typeof erreur === 'string' ? erreur : ''
  for (const [code, phrase] of Object.entries(MESSAGES_FACTURATION)) if (message.includes(code)) return phrase
  if (/Could not find the function|PGRST202|function .* does not exist/i.test(message))
    return 'Disponible après la mise à jour de la base (migration 0032).'
  return message || 'Erreur inconnue.'
}

/** Le résumé de ce qui vient d'être fait, pour la notification. */
export function resumeGeneration(r: RapportFacturation): string {
  const n = r.factures.length
  const base = `${n} facture${n > 1 ? 's' : ''} ${r.ecrit ? 'générée' : 'prévue'}${n > 1 ? 's' : ''} pour ${r.libelle_mois || libelleMois(r.mois)}`
  const erreurs = r.ignores.filter((i) => i.motif === 'erreur').length
  return erreurs > 0 ? `${base} — ${erreurs} client${erreurs > 1 ? 's' : ''} en erreur, à voir.` : base
}

/** Les clients sautés qui laissent des courses non facturées : ce que le comptable doit traiter à la main. */
export function coursesLaisseesDeCote(r: RapportFacturation): number {
  return r.ignores
    .filter((i) => i.motif === 'sans_abonnement' || i.motif === 'rien_a_facturer')
    .reduce((s, i) => s + i.courses_non_facturees, 0)
}
